import React, { useEffect, useRef, useState } from 'react';
import { Loader2, MapPin, Search, X } from 'lucide-react';
import {
    autocompletePlaces,
    createPlaceSearchSessionToken,
    getPlaceSearchLocation,
    PlaceSearchLocation,
    PlaceSearchSuggestion,
} from '../lib/api';

interface ParkingLotMapSearchProps {
    selectedCity: string;
    onSelect: (location: PlaceSearchLocation, label: string) => void;
    onClear: () => void;
}

const ParkingLotMapSearch: React.FC<ParkingLotMapSearchProps> = ({ selectedCity, onSelect, onClear }) => {
    const [query, setQuery] = useState('');
    const [suggestions, setSuggestions] = useState<PlaceSearchSuggestion[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [activeIndex, setActiveIndex] = useState(-1);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const requestRef = useRef<AbortController | null>(null);
    const sessionTokenRef = useRef<string | null>(null);

    useEffect(() => () => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        requestRef.current?.abort();
    }, []);

    const clear = () => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        requestRef.current?.abort();
        sessionTokenRef.current = null;
        setQuery('');
        setSuggestions([]);
        setIsOpen(false);
        setIsLoading(false);
        setError(null);
        setActiveIndex(-1);
        onClear();
    };

    const runAutocomplete = (value: string) => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        requestRef.current?.abort();
        const trimmed = value.trim();
        if (trimmed.length < 2) {
            sessionTokenRef.current = null;
            setSuggestions([]);
            setIsOpen(false);
            setIsLoading(false);
            setError(null);
            return;
        }

        setIsLoading(true);
        setError(null);
        debounceRef.current = setTimeout(async () => {
            const controller = new AbortController();
            requestRef.current = controller;
            const sessionToken = sessionTokenRef.current ?? createPlaceSearchSessionToken();
            sessionTokenRef.current = sessionToken;
            try {
                const results = await autocompletePlaces(trimmed, selectedCity, sessionToken, controller.signal);
                setSuggestions(results);
                setActiveIndex(results.length > 0 ? 0 : -1);
                setIsOpen(true);
            } catch (caught) {
                if (controller.signal.aborted) return;
                setSuggestions([]);
                setIsOpen(true);
                setError(caught instanceof Error ? caught.message : 'Søgningen kunne ikke gennemføres');
            } finally {
                if (!controller.signal.aborted) setIsLoading(false);
            }
        }, 250);
    };

    const selectSuggestion = async (suggestion: PlaceSearchSuggestion) => {
        requestRef.current?.abort();
        const controller = new AbortController();
        requestRef.current = controller;
        setQuery(suggestion.description);
        setSuggestions([]);
        setIsOpen(false);
        setIsLoading(true);
        setError(null);
        try {
            const location = await getPlaceSearchLocation(
                suggestion.placeId,
                sessionTokenRef.current ?? '',
                controller.signal
            );
            onSelect(location, location.formattedAddress || suggestion.description);
            sessionTokenRef.current = null;
        } catch (caught) {
            if (!controller.signal.aborted) {
                setError(caught instanceof Error ? caught.message : 'Stedet kunne ikke åbnes');
                setIsOpen(true);
            }
        } finally {
            if (!controller.signal.aborted) setIsLoading(false);
        }
    };

    return (
        <div className="absolute left-1/2 top-3 z-30 w-[calc(100%-1.5rem)] max-w-md -translate-x-1/2">
            <div className="flex items-center gap-2 rounded-full border border-gray-200 bg-white/95 px-4 py-2 shadow-lg backdrop-blur-sm">
                <Search className="h-4 w-4 shrink-0 text-gray-500" />
                <input
                    type="text"
                    role="combobox"
                    aria-label="Søg efter adresse eller sted"
                    aria-autocomplete="list"
                    aria-controls="parking-lot-map-search-results"
                    aria-expanded={isOpen}
                    value={query}
                    onChange={(event) => {
                        const value = event.target.value;
                        setQuery(value);
                        runAutocomplete(value);
                    }}
                    onFocus={() => {
                        if (suggestions.length > 0 || error) setIsOpen(true);
                    }}
                    onBlur={() => setIsOpen(false)}
                    onKeyDown={(event) => {
                        if (event.key === 'ArrowDown' && suggestions.length > 0) {
                            event.preventDefault();
                            setActiveIndex((current) => Math.min(current + 1, suggestions.length - 1));
                        } else if (event.key === 'ArrowUp' && suggestions.length > 0) {
                            event.preventDefault();
                            setActiveIndex((current) => Math.max(current - 1, 0));
                        } else if (event.key === 'Enter' && activeIndex >= 0 && suggestions[activeIndex]) {
                            event.preventDefault();
                            void selectSuggestion(suggestions[activeIndex]);
                        } else if (event.key === 'Escape') {
                            setIsOpen(false);
                        }
                    }}
                    placeholder="Søg efter adresse eller sted…"
                    className="min-w-0 flex-1 bg-transparent text-sm text-gray-800 outline-none placeholder:text-gray-400"
                />
                {isLoading && <Loader2 className="h-4 w-4 shrink-0 animate-spin text-gray-500" />}
                {(query || suggestions.length > 0) && !isLoading && (
                    <button type="button" onClick={clear} aria-label="Ryd søgning" className="text-gray-400 hover:text-gray-700">
                        <X className="h-4 w-4" />
                    </button>
                )}
            </div>

            {isOpen && (
                <div
                    id="parking-lot-map-search-results"
                    role="listbox"
                    className="mt-2 max-h-64 overflow-y-auto rounded-xl border border-gray-100 bg-white shadow-xl"
                >
                    {suggestions.map((suggestion, index) => (
                        <button
                            key={suggestion.placeId}
                            type="button"
                            role="option"
                            aria-selected={activeIndex === index}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => void selectSuggestion(suggestion)}
                            className={`flex w-full items-start gap-2 border-b border-gray-50 px-4 py-2.5 text-left text-sm last:border-0 ${
                                activeIndex === index ? 'bg-blue-50 text-blue-900' : 'text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                            <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                            <span>{suggestion.description}</span>
                        </button>
                    ))}
                    {!isLoading && suggestions.length === 0 && (
                        <div className={`px-4 py-3 text-sm ${error ? 'text-red-700' : 'text-gray-500'}`}>
                            {error || 'Ingen steder fundet'}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ParkingLotMapSearch;
